/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';

const DEFAULT_MBOX = 'target-home-layout';
const DEFAULT_VARIANT = 'B';
const DEFAULT_FALLBACK_VARIANT = 'A';
const DEFAULT_TIMEOUT_MS = 10000;
const DEFAULT_INTERVAL_MS = 100;
const DEFAULT_CLICK_SELECTOR = 'a, button';

const wait = (delay) => new Promise((resolve) => {
    setTimeout(resolve, delay);
});

const normalizeVariant = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim().toUpperCase();

    if (!normalized) {
        return null;
    }

    if (/^[A-Z0-9_-]+$/.test(normalized)) {
        return normalized;
    }

    return null;
};

const tryParseJson = (value) => {
    if (typeof value !== 'string' || !/^[[{]/.test(value.trim())) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        console.warn('Adobe Target variant parse error', error);
        return null;
    }
};

const extractVariantFromArray = (value) => {
    for (const item of value) {
        const variant = extractVariantFromValue(item);
        if (variant) {
            return variant;
        }
    }

    return null;
};

const extractVariantFromObject = (value) => {
    const priorityKeys = ['variant', 'content', 'value', 'name'];

    for (const key of priorityKeys) {
        if (Object.hasOwn(value, key)) {
            const variant = extractVariantFromValue(value[key]);
            if (variant) {
                return variant;
            }
        }
    }

    return extractVariantFromArray(Object.values(value));
};

const extractVariantFromValue = (value) => {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const normalized = normalizeVariant(value);
        if (normalized) {
            return normalized;
        }

        return extractVariantFromValue(tryParseJson(value));
    }

    if (Array.isArray(value)) {
        return extractVariantFromArray(value);
    }

    if (typeof value === 'object') {
        return extractVariantFromObject(value);
    }

    return null;
};

const waitForAdobeTarget = async (timeout = DEFAULT_TIMEOUT_MS, intervalMs = DEFAULT_INTERVAL_MS) => {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
        const target = globalThis?.window?.adobe?.target;

        if (target) {
            return target;
        }

        await wait(intervalMs);
    }

    return null;
};

const getTargetVariant = async ({ mbox, fallbackVariant, timeoutMs, intervalMs }) => {
    const target = await waitForAdobeTarget(timeoutMs, intervalMs);

    if (!target) {
        return fallbackVariant;
    }

    return new Promise((resolve) => {
        try {
            if (typeof target.getOffer === 'function') {
                target.getOffer({
                    mbox,
                    success: (offer) => resolve(extractVariantFromValue(offer) || fallbackVariant),
                    error: () => resolve(fallbackVariant)
                });
                return;
            }

            if (typeof target.getOffers === 'function') {
                target.getOffers({
                    request: {
                        execute: {
                            mboxes: [{
                                name: mbox,
                                index: 0
                            }]
                        }
                    },
                    success: (offer) => resolve(extractVariantFromValue(offer) || fallbackVariant),
                    error: () => resolve(fallbackVariant)
                });
                return;
            }

            resolve(fallbackVariant);
        } catch (error) {
            console.warn('Adobe Target getOffer error', error);
            resolve(fallbackVariant);
        }
    });
};

const normalizeTrackingText = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.replaceAll(/\s+/g, ' ').trim().slice(0, 120);
};

const getTrackingText = (targetElement) => {
    const titleElement = targetElement.querySelector?.('h3');

    if (titleElement?.textContent) {
        return normalizeTrackingText(titleElement.textContent);
    }

    return normalizeTrackingText(targetElement.textContent);
};

const buildTrackingParams = ({ mbox, variant, targetElement }) => {
    return {
        decisionMbox: mbox,
        variant,
        elementTag: targetElement.tagName?.toLowerCase() || '',
        elementId: targetElement.id || '',
        elementText: getTrackingText(targetElement),
        elementHref: targetElement.getAttribute?.('href') || '',
        elementAriaLabel: targetElement.getAttribute?.('aria-label') || ''
    };
};

const attachClickTracking = ({ movableElement, clickSelector, clickMbox, mbox, variant }) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (!movableElement || !clickMbox || typeof trackEvent !== 'function') {
        console.warn('Adobe Target Move: click tracking unavailable', {
            hasMovableElement: !!movableElement,
            clickMbox,
            hasTrackEvent: typeof trackEvent === 'function'
        });
        return () => {};
    }

    console.log('Adobe Target Move: click tracking attached', {
        decisionMbox: mbox,
        clickMbox,
        clickSelector
    });

    const handleClick = (event) => {
        const targetElement = event.target?.closest?.(clickSelector);

        if (!targetElement || !movableElement.contains(targetElement)) {
            return;
        }

        const params = buildTrackingParams({
            mbox,
            variant,
            targetElement
        });

        console.log('Adobe Target Move: click tracked', {
            clickMbox,
            params
        });

        trackEvent({
            mbox: clickMbox,
            params,
            success: () => {
                console.log('Adobe Target Move: trackEvent success', {
                    clickMbox,
                    params
                });
            },
            error: (status, error) => {
                console.warn('Adobe Target Move: trackEvent error', {
                    clickMbox,
                    status,
                    error,
                    params
                });
            }
        });
    };

    movableElement.addEventListener('click', handleClick);

    return () => {
        movableElement.removeEventListener('click', handleClick);
    };
};

const refreshSwipers = (rootElement) => {
    if (!rootElement || typeof rootElement.querySelectorAll !== 'function') {
        return;
    }

    const swiperElements = rootElement.matches?.('.swiper')
        ? [rootElement, ...rootElement.querySelectorAll('.swiper')]
        : rootElement.querySelectorAll('.swiper');

    swiperElements.forEach((swiperElement) => {
        swiperElement?.swiper?.update?.();
        swiperElement?.swiper?.updateSlides?.();
        swiperElement?.swiper?.updateProgress?.();
        swiperElement?.swiper?.updateSize?.();
    });
};

const getMovableElement = ({ sourceSelector, sourceClosestSelector }) => {
    const sourceElement = document.querySelector(sourceSelector);

    if (!sourceElement) {
        return null;
    }

    if (!sourceClosestSelector) {
        return sourceElement;
    }

    return sourceElement.closest(sourceClosestSelector);
};

const moveElementToAnchor = ({ anchorElement, movableElement, position, refreshSwiper: shouldRefreshSwiper }) => {
    if (position === 'after') {
        anchorElement.parentNode.insertBefore(movableElement, anchorElement.nextSibling);
    } else {
        anchorElement.parentNode.insertBefore(movableElement, anchorElement);
    }

    if (shouldRefreshSwiper) {
        refreshSwipers(movableElement);
    }
};

const waitForMoveTarget = async ({ anchorRef, sourceSelector, sourceClosestSelector, waitTimeoutMs, waitIntervalMs, isCancelledRef }) => {
    const startTime = Date.now();

    while (!isCancelledRef.current && Date.now() - startTime < waitTimeoutMs) {
        const anchorElement = anchorRef.current;
        const movableElement = getMovableElement({ sourceSelector, sourceClosestSelector });

        if (anchorElement?.parentNode && movableElement) {
            return { anchorElement, movableElement };
        }

        await wait(waitIntervalMs);
    }

    return null;
};

const waitForVariantSelectors = async ({ variantSelectors, waitTimeoutMs, waitIntervalMs, isCancelledRef }) => {
    const startTime = Date.now();
    const selectors = Object.values(variantSelectors);

    while (!isCancelledRef.current && Date.now() - startTime < waitTimeoutMs) {
        const allExist = selectors.every((selector) => document.querySelector(selector));
        if (allExist) {
            return true;
        }
        await wait(waitIntervalMs);
    }

    console.warn('Adobe Target SelectorElement: timeout waiting for variant selectors', variantSelectors);
    return false;
};

const hideVariantsBySelectors = (variantSelectors) => {
    Object.values(variantSelectors).forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
        });
    });
};

const showVariantBySelector = (selector) => {
    document.querySelectorAll(selector).forEach((el) => {
        el.style.display = '';
    });
};

const attachDelegatedClickTracking = ({ variantSelectors, clickSelector, clickMbox, mbox, variant }) => {
    const trackEvent = globalThis?.window?.adobe?.target?.trackEvent;

    if (!clickMbox || typeof trackEvent !== 'function') {
        console.warn('Adobe Target SelectorElement: click tracking unavailable', {
            clickMbox,
            hasTrackEvent: typeof trackEvent === 'function'
        });
        return () => {};
    }

    const allSelectors = Object.values(variantSelectors).join(', ');

    console.log('Adobe Target SelectorElement: delegated click tracking attached', {
        decisionMbox: mbox,
        clickMbox,
        allSelectors
    });

    const handleClick = (event) => {
        const variantContainer = event.target?.closest?.(allSelectors);
        if (!variantContainer) return;

        const targetElement = event.target?.closest?.(clickSelector) || variantContainer;

        const params = buildTrackingParams({
            mbox,
            variant,
            targetElement
        });

        console.log('Adobe Target SelectorElement: click tracked', { clickMbox, params });

        trackEvent({
            mbox: clickMbox,
            params,
            success: () => {
                console.log('Adobe Target SelectorElement: trackEvent success', { clickMbox, params });
            },
            error: (status, error) => {
                console.warn('Adobe Target SelectorElement: trackEvent error', { clickMbox, status, error, params });
            }
        });
    };

    document.addEventListener('click', handleClick);

    return () => document.removeEventListener('click', handleClick);
};

function AdobeTargetMove(props) {
    const {
        mbox = DEFAULT_MBOX,
        expectedVariant = DEFAULT_VARIANT,
        fallbackVariant = DEFAULT_FALLBACK_VARIANT,
        sourceSelector,
        sourceClosestSelector,
        position = 'before',
        waitTimeoutMs = DEFAULT_TIMEOUT_MS,
        waitIntervalMs = DEFAULT_INTERVAL_MS,
        refreshSwiper = true,
        clickMbox = null,
        clickSelector = DEFAULT_CLICK_SELECTOR,
        variantSelectors = null
    } = props;
    const anchorRef = useRef(null);
    const movedElementRef = useRef(null);
    const originalPlacementRef = useRef(null);
    const isCancelledRef = useRef(false);
    const detachTrackingRef = useRef(() => {});

    useEffect(() => {
        if (!sourceSelector || globalThis.window === undefined) {
            return undefined;
        }

        isCancelledRef.current = false;

        const moveWhenReady = async () => {
            const variant = await getTargetVariant({
                mbox,
                fallbackVariant,
                timeoutMs: waitTimeoutMs,
                intervalMs: waitIntervalMs
            });

            console.log(`Adobe Target Move: mbox=${mbox}, expectedVariant=${expectedVariant}, actualVariant=${variant}`);
            if (isCancelledRef.current) {
                return;
            }

            const moveTarget = await waitForMoveTarget({
                anchorRef,
                sourceSelector,
                sourceClosestSelector,
                waitTimeoutMs,
                waitIntervalMs,
                isCancelledRef
            });

            if (!moveTarget) {
                return;
            }

            const { anchorElement, movableElement } = moveTarget;

            detachTrackingRef.current();
            detachTrackingRef.current = attachClickTracking({
                movableElement,
                clickSelector,
                clickMbox,
                mbox,
                variant
            });

            movedElementRef.current = movableElement;

            if (variant !== expectedVariant) {
                return;
            }

            if (!originalPlacementRef.current) {
                originalPlacementRef.current = {
                    parent: movableElement.parentNode,
                    nextSibling: movableElement.nextSibling
                };
            }

            moveElementToAnchor({
                anchorElement,
                movableElement,
                position,
                refreshSwiper
            });
        };

        moveWhenReady();

        return () => {
            isCancelledRef.current = true;
            detachTrackingRef.current();
            detachTrackingRef.current = () => {};

            const movableElement = movedElementRef.current;
            const originalPlacement = originalPlacementRef.current;

            if (movableElement && originalPlacement?.parent?.isConnected) {
                originalPlacement.parent.insertBefore(movableElement, originalPlacement.nextSibling);
            }
        };
    }, [mbox, expectedVariant, fallbackVariant, sourceSelector, sourceClosestSelector, position, waitTimeoutMs, waitIntervalMs, refreshSwiper, clickMbox, clickSelector]);

    useEffect(() => {
        if (!variantSelectors || globalThis.window === undefined) {
            return undefined;
        }

        isCancelledRef.current = false;

        const selectWhenReady = async () => {
            // Esperar AT i els elements del DOM en paral·lel
            const [variant, domReady] = await Promise.all([
                getTargetVariant({
                    mbox,
                    fallbackVariant,
                    timeoutMs: waitTimeoutMs,
                    intervalMs: waitIntervalMs
                }),
                waitForVariantSelectors({
                    variantSelectors,
                    waitTimeoutMs,
                    waitIntervalMs,
                    isCancelledRef
                })
            ]);

            console.log(`Adobe Target SelectorElement: mbox=${mbox}, actualVariant=${variant}, domReady=${domReady}`);

            if (isCancelledRef.current || !domReady) {
                return;
            }

            // Amagar tots i mostrar el guanyador
            hideVariantsBySelectors(variantSelectors);
            if (variantSelectors[variant]) {
                showVariantBySelector(variantSelectors[variant]);
            }

            // Attach event delegation per ambdós variants
            if (clickMbox) {
                detachTrackingRef.current();
                detachTrackingRef.current = attachDelegatedClickTracking({
                    variantSelectors,
                    clickSelector,
                    clickMbox,
                    mbox,
                    variant
                });
            }
        };

        selectWhenReady();

        return () => {
            isCancelledRef.current = true;
            detachTrackingRef.current();
            detachTrackingRef.current = () => {};
        };
    }, [mbox, fallbackVariant, variantSelectors, waitTimeoutMs, waitIntervalMs, clickMbox, clickSelector]);

    return (
        <div
            ref={anchorRef}
            aria-hidden='true'
            data-component='AdobeTargetMove'
            data-mbox={mbox}
            data-source-selector={sourceSelector || ''}
            style={{ display: 'none' }}
        />
    );
}

export default AdobeTargetMove;